import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, Spin, Tooltip, Typography } from "antd";
import {
  CloseOutlined,
  DownloadOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileZipOutlined,
  FullscreenExitOutlined,
  LeftOutlined,
  RightOutlined,
  SoundOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { useAudioPlayerContext } from "../../../context/AudioPlayerContext";
import "./index.scss";

const { Text } = Typography;

export interface MediaItem {
  id: string | number;
  mediaType: 1 | 2 | 3 | 4; // 1=image 2=video 3=document 4=audio
  filePath: string;
  fileName?: string;
  senderName?: string;
  timestamp?: string;
}

interface MediaViewerProps {
  open: boolean;
  items: MediaItem[];
  currentIndex: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function extractFileName(path: string): string {
  try {
    return new URL(path).pathname.split("/").pop() || "file";
  } catch {
    return path.split("/").pop() || "file";
  }
}

function FileIcon({ path }: { path: string }) {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return <FilePdfOutlined />;
  if (["zip", "rar", "7z", "gz"].includes(ext)) return <FileZipOutlined />;
  return <FileOutlined />;
}

// ── thumbnail ─────────────────────────────────────────────────────────────────

const Thumbnail: React.FC<{
  item: MediaItem;
  active: boolean;
  onClick: () => void;
}> = ({ item, active, onClick }) => (
  <div
    className={`mv-thumb${active ? " mv-thumb--active" : ""}`}
    onClick={onClick}
    role="button"
    aria-label="Select media"
  >
    {item.mediaType === 1 && (
      <img src={item.filePath} alt="" draggable={false} />
    )}
    {item.mediaType === 2 && (
      <video src={item.filePath} muted preload="metadata" />
    )}
    {item.mediaType === 4 && (
      <div className="mv-thumb-icon">
        <SoundOutlined />
      </div>
    )}
    {item.mediaType === 3 && (
      <div className="mv-thumb-icon">
        <FileIcon path={item.filePath} />
      </div>
    )}
  </div>
);

// ── main component ────────────────────────────────────────────────────────────

const MediaViewer: React.FC<MediaViewerProps> = ({
  open,
  items,
  currentIndex,
  onClose,
  onIndexChange,
}) => {
  const { pause: pauseContextAudio } = useAudioPlayerContext();

  const [scale, setScale] = useState(1);
  const [imgLoading, setImgLoading] = useState(false);
  const [animDir, setAnimDir] = useState<"left" | "right" | null>(null);
  const thumbStripRef = useRef<HTMLDivElement>(null);

  const current = items[currentIndex];

  const goTo = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= items.length) return;
      setScale(1);
      setImgLoading(items[idx]?.mediaType === 1);
      setAnimDir(idx > currentIndex ? "right" : "left");
      onIndexChange(idx);
    },
    [currentIndex, items, onIndexChange],
  );

  const goNext = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);
  const goPrev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, goNext, goPrev]);

  // Stop context-managed audio when viewer opens
  useEffect(() => {
    if (!open) return;
    pauseContextAudio();
  }, [open, pauseContextAudio]);

  // Scroll active thumbnail into view
  useEffect(() => {
    if (!thumbStripRef.current) return;
    const el = thumbStripRef.current.children[currentIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [currentIndex]);

  // Pause every other audio/video in the page when one starts playing
  const handleMediaPlay = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement | HTMLAudioElement>) => {
      pauseContextAudio();
      const playing = e.currentTarget;
      document
        .querySelectorAll<HTMLVideoElement | HTMLAudioElement>("audio, video")
        .forEach((el) => { if (el !== playing && !el.paused) el.pause(); });
    },
    [pauseContextAudio],
  );

  const handleDownload = useCallback(() => {
    if (!current?.filePath) return;
    const a = document.createElement("a");
    a.href = current.filePath;
    a.download = current.fileName || extractFileName(current.filePath);
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [current]);

  // ── media renderers ─────────────────────────────────────────────────────────

  const renderMedia = () => {
    if (!current) return null;

    const key = `${current.id}-${current.filePath}`;

    switch (current.mediaType) {
      case 1:
        return (
          <div
            className="mv-img-wrapper"
            style={{ transform: `scale(${scale})` }}
          >
            {imgLoading && <Spin className="mv-loader" size="large" />}
            <img
              key={key}
              src={current.filePath}
              alt={current.fileName || "image"}
              draggable={false}
              style={{ opacity: imgLoading ? 0 : 1 }}
              onLoad={() => setImgLoading(false)}
              onError={() => setImgLoading(false)}
            />
          </div>
        );

      case 2:
        return (
          <div className="mv-video-wrapper">
            <video
              key={key}
              className="mv-video"
              controls
              autoPlay
              onPlay={handleMediaPlay}
            >
              <source src={current.filePath} />
              Your browser does not support this video format.
            </video>
          </div>
        );

      case 4:
        return (
          <div className="mv-audio-wrapper">
            <SoundOutlined className="mv-audio-icon" />
            <Text className="mv-audio-label">
              {current.fileName || extractFileName(current.filePath)}
            </Text>
            <audio
              key={key}
              controls
              className="mv-audio"
              controlsList="nodownload noplaybackrate"
              onPlay={handleMediaPlay}
            >
              <source src={current.filePath} />
            </audio>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              className="mv-doc-download-btn"
            >
              Download
            </Button>
          </div>
        );

      case 3:
      default:
        return (
          <div className="mv-doc-wrapper">
            <div className="mv-doc-icon">
              <FileIcon path={current.filePath} />
            </div>
            <Text className="mv-doc-name">
              {current.fileName || extractFileName(current.filePath)}
            </Text>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              className="mv-doc-download-btn"
            >
              Download File
            </Button>
          </div>
        );
    }
  };

  if (!open || !current) return null;

  const fileName = current.fileName || extractFileName(current.filePath);
  const stageClass = [
    "mv-stage",
    animDir === "right" ? "mv-stage--slide-right" : "",
    animDir === "left" ? "mv-stage--slide-left" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="media-viewer-overlay" onClick={onClose}>
      {/* ── Header ── */}
      <div className="mv-header" onClick={(e) => e.stopPropagation()}>
        <div className="mv-header-meta">
          {current.senderName && (
            <Text className="mv-meta-sender">{current.senderName}</Text>
          )}
          <Text className="mv-meta-filename">{fileName}</Text>
          {current.timestamp && (
            <Text className="mv-meta-time">{current.timestamp}</Text>
          )}
        </div>

        <div className="mv-header-actions">
          {current.mediaType === 1 && (
            <>
              <Tooltip title="Zoom in (+)">
                <Button
                  type="text"
                  icon={<ZoomInOutlined />}
                  className="mv-btn"
                  onClick={() => setScale((s) => Math.min(s + 0.25, 4))}
                />
              </Tooltip>
              <Tooltip title="Zoom out (-)">
                <Button
                  type="text"
                  icon={<ZoomOutOutlined />}
                  className="mv-btn"
                  onClick={() => setScale((s) => Math.max(s - 0.25, 0.25))}
                />
              </Tooltip>
              <Tooltip title="Reset zoom">
                <Button
                  type="text"
                  icon={<FullscreenExitOutlined />}
                  className="mv-btn"
                  onClick={() => setScale(1)}
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="Download">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              className="mv-btn"
              onClick={handleDownload}
            />
          </Tooltip>
          <Tooltip title="Close (Esc)">
            <Button
              type="text"
              icon={<CloseOutlined />}
              className="mv-btn mv-btn--close"
              onClick={onClose}
            />
          </Tooltip>
        </div>
      </div>

      {/* ── Main stage ── */}
      <div className="mv-content" onClick={(e) => e.stopPropagation()}>
        {currentIndex > 0 && (
          <Button
            type="text"
            icon={<LeftOutlined />}
            className="mv-nav mv-nav--prev"
            onClick={goPrev}
            aria-label="Previous"
          />
        )}

        <div className={stageClass} onAnimationEnd={() => setAnimDir(null)}>{renderMedia()}</div>

        {currentIndex < items.length - 1 && (
          <Button
            type="text"
            icon={<RightOutlined />}
            className="mv-nav mv-nav--next"
            onClick={goNext}
            aria-label="Next"
          />
        )}
      </div>

      {/* ── Counter ── */}
      <div className="mv-counter" onClick={(e) => e.stopPropagation()}>
        {currentIndex + 1} / {items.length}
      </div>

      {/* ── Thumbnail strip ── */}
      {items.length > 1 && (
        <div
          className="mv-thumb-strip"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mv-thumb-track" ref={thumbStripRef}>
            {items.map((item, idx) => (
              <Thumbnail
                key={item.id}
                item={item}
                active={idx === currentIndex}
                onClick={() => goTo(idx)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaViewer;
